import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { formatReportDate } from "../../../../utils/reportDateUtils";
import { missingAttendanceHours } from "../../../../utils/attendanceDuration";

// Third-party
import { TableColumn } from "react-data-table-component";

// Local utils
import { getTimeDifference } from "../../../../utils/timeUtils";
import { dateConversion } from "../../../../utils/dateUtils";

// Local constants
import {
  Action,
  InTime,
  OutTime,
  WorkingHours,
  Name,
  admin,
  hr,
  present,
  absent,
  casualLeave,
  attendanceCorrectionModal,
  infoColor,
  danger,
  secondary,
  success,
  warningColor,
  NA,
} from "../../../../utils/Constant";

// Hooks
import useUser from "../../../../hooks/useUser";

// Components
import IconComponent from "../../../../CommonElements/IconComponent";
import BadgeDetails from "../../../../CommonElements/Badge/BadgeDetails";
import { P } from "../../../../AbstractElements";
import CustomDataTable from "../../../../CommonElements/Table/CustomTable";
import SkeletonTable from "../../../../CommonElements/Loader/SkeletonTableLoader";

// Redux slices / thunks
import { openModal } from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { fetchAssginedManagerToEmployee } from "../../../hrZone/Users/redux/slice/AssignedManagerToEmployeeSlice";

// Types
import { ModalType } from "../../../../Types/UserProfileDetails";
import { IAttendanceDetailsResponse, IAttendanceTableProps } from "../../types";
import LocationTimeCell from "../locationTimeCell/LocationTimeCell";

const isEmptyAttendance = (row: IAttendanceDetailsResponse): boolean =>
  !row.in_time &&
  !row.out_time &&
  !row.editable &&
  !["present", "absent"].includes(
    row.remark_all?.toLowerCase()?.replaceAll(" ", ""),
  );

const badgeColorMap: Record<string, string> = {
  [present]: success,
  [absent]: danger,
  [casualLeave]: secondary,
  sunday: infoColor,
  correctionrequestsent: "light text-dark",
};

const getBadgeColor = (remark = ""): string => {
  const formatted = remark.toLowerCase().replaceAll(" ", "");
  return badgeColorMap[formatted] || warningColor;
};

const AttendanceTable = ({
  dispatch,
  attendanceLoading,
  employeeAttendanceDetails,
}: IAttendanceTableProps) => {
  const user = useUser();
  const clean = useLocation().pathname === "/employee-zone/view-attendance";
  const [inTooltip, setInTooltip] = useState<Record<number, boolean>>({});
  const [outTooltip, setOutTooltip] = useState<Record<number, boolean>>({});

  const toggleTooltip = useCallback((index: number, isIn: boolean) => {
    (isIn ? setInTooltip : setOutTooltip)((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  }, []);

  const handleOpenModal = useCallback(
    (modalKey: ModalType, selectedId?: string | number | null) => {
      dispatch(openModal({ modalType: modalKey, selectedId }));
    },
    [dispatch],
  );

  const handleEditClick = useCallback(
    (row: IAttendanceDetailsResponse) => {
      dispatch(fetchAssginedManagerToEmployee({ id: Number(row.user_id) }));
      handleOpenModal(attendanceCorrectionModal, row.date);
    },
    [dispatch, handleOpenModal],
  );

  // Columns
  const columns = useMemo<TableColumn<IAttendanceDetailsResponse>[]>(() => {
    const baseCols: TableColumn<IAttendanceDetailsResponse>[] = [
      {
        name: "#",
        cell: (_, index) => <P className="m-0">{index + 1}</P>,
        sortable: true,
        width: "60px",
      },
      ...(user?.user_type && [hr, admin].includes(user.user_type)
        ? [
            {
              name: Name,
              cell: (row: IAttendanceDetailsResponse) => (
                <P className="m-0 f-12">{row.emp_name}</P>
              ),
              sortable: true,
              grow: 1,
            },
          ]
        : []),
      {
        name: "Date",
        cell: (row) => {
          if (clean) return <span className="attendance-clean-date">{formatReportDate(row.date)}</span>;
          const isDateGreater =
            new Date().toISOString() > new Date(row.date).toISOString();

          const remark = String(row?.remark_all)
            ?.replaceAll(" ", "")
            ?.trim()
            ?.toLowerCase();
          return (
            <div
              className={
                ["present", "absent", "sunday"].includes(remark)
                  ? "d-flex align-items-center"
                  : "d-flex flex-column align-items-start"
              }
            >
              <P className="m-0 f-12">{dateConversion(row.date, true)}</P>

              {isDateGreater && (
                <div
                  className={
                    ["present", "absent", "sunday"].includes(remark)
                      ? "ms-2"
                      : "m-0"
                  }
                >
                  <BadgeDetails
                    color={getBadgeColor(row.remark_all)}
                    className="badget-success rounded-pill"
                    text={row.remark_all ?? ""}
                  />
                </div>
              )}
            </div>
          );
        },
        sortable: true,
        grow: 2,
      },
      {
        name: InTime,
        cell: (row, index) =>
          isEmptyAttendance(row) ? null : (
            <LocationTimeCell
              clean={clean}
              time={row.in_time ?? ""}
              location={row.in_location ?? ""}
              tooltipId={`tooltip-in-location-${index}`}
              showTooltip={inTooltip[index] || false}
              toggleTooltip={() => toggleTooltip(index, true)}
            />
          ),
        sortable: true,
        grow: 1,
      },
      {
        name: OutTime,
        cell: (row, index) =>
          isEmptyAttendance(row) ? null : (
            <LocationTimeCell
              clean={clean}
              time={row.out_time ?? ""}
              location={row.out_location ?? ""}
              tooltipId={`tooltip-out-location-${index}`}
              showTooltip={outTooltip[index] || false}
              toggleTooltip={() => toggleTooltip(index, false)}
            />
          ),
        sortable: true,
        grow: 1,
      },
      {
        name: WorkingHours,
        cell: (row) => {
          if (isEmptyAttendance(row)) return null;
          const hours = getTimeDifference(
            row.in_time ?? "",
            row.out_time ?? "",
          );
          return (
            <div className="d-flex align-items-center">
              <P className="m-0 f-12">{hours}</P>
              {!clean && row.in_time && row.out_time && hours <= "9:00" && (
                <IconComponent className="fa fa-flag text-danger f-14 ms-2" />
              )}
            </div>
          );
        },
        grow: 1,
      },
      ...([
        ["deficit_hours", "Deficit Hours"],
      ] as const).map(([key, label]): TableColumn<IAttendanceDetailsResponse> => ({
        name: label,
        selector: (row) => row[key] ?? NA,
        cell: (row) => <P className={`m-0 f-12 ${clean && row[key] ? "text-danger" : ""}`}>{row[key] != null ? (clean ? missingAttendanceHours(row[key]!, 0) : `${row[key]} hrs`) : (clean ? "–" : NA)}</P>,
        sortable: true,
        minWidth: "110px",
        grow: 1,
      })),
      {
        name: Action,
        cell: (row) =>
          row.editable && !isEmptyAttendance(row) ? (
            clean ? <button type="button" className="attendance-correction-button" aria-label={`Request correction for ${formatReportDate(row.date)}`} title="Request correction" onClick={() => handleEditClick(row)}><IconComponent className="fa fa-edit" /></button> :
            <IconComponent
              className="fa fa-edit text-primary f-16 pointer"
              onClick={() => handleEditClick(row)}
            />
          ) : null,
        grow: 1,
      },
    ];
    if (clean) {
      const cleanColumns = baseCols.filter((column) => column.name !== "#" && column.name !== Name);
      cleanColumns.splice(cleanColumns.length - 1, 0, {
        name: "Status",
        cell: (row) => <span className={`attendance-clean-status status-${row.remark_all?.toLowerCase().replace(/[^a-z]/g, "")}`}>{row.remark_all || "–"}</span>,
        grow: 1,
      });
      return cleanColumns;
    }
    return baseCols;
  }, [clean, user?.user_type, inTooltip, outTooltip, handleEditClick, toggleTooltip]);

  // Table Data with row index
  const tableData = useMemo<
    (IAttendanceDetailsResponse & { _rowClass: string })[]
  >(() => {
    const rows = Array.isArray(employeeAttendanceDetails)
      ? employeeAttendanceDetails
      : employeeAttendanceDetails?.data || [];

    return rows
      .slice()
      .reverse()
      .map((row, i) => ({
        ...row,
        _rowClass: `row-animate row-delay-${i}`,
      }));
  }, [employeeAttendanceDetails]);

  return (
    <div className="table-responsive user-datatable custom-scrollbar mt-4 d-none d-lg-block">
      <CustomDataTable
        columns={columns}
        data={tableData}
        loading={attendanceLoading}
        pageSize={60}
        loaderComponent={
          <SkeletonTable
            rows={tableData.length || 8}
            columns={columns}
            rowHeight={40}
            gap={4}
          />
        }
      />
    </div>
  );
};

export default AttendanceTable;
